import { OSSchema } from "@sloe/schema";
import { DEFAULT_BRAND_COLOR } from "../../lib/brand";

// ═══════════════════════════════════════════════════════════════════════
// WELCOME SCREEN (P2 CP6)
// ═══════════════════════════════════════════════════════════════════════
// One-time full-screen takeover shown when the user first lands on their Agentic Operating System
// with ?welcome=true (set by the claim redirect). SchemaRenderer renders this
// INSTEAD of the normal UI; "Open Operator" routes to the Operator screen and
// strips the param — from there P1's first-run walkthrough takes over.
//
// Charter-aware copy is read straight from the schema metadata:
//   name           → the business name
//   industry_shape → the industry (set from architectState.industry_signal;
//                     the same field ContextAssembler feeds the Operator as
//                     `industry`). No charter fetch — the data's already here.
// ═══════════════════════════════════════════════════════════════════════

interface WelcomeScreenProps {
  schema: OSSchema;
  onEnter: () => void;
}

export function WelcomeScreen({ schema, onEnter }: WelcomeScreenProps) {
  const businessName = schema.metadata?.name?.trim() || "your workspace";
  const industry = (schema.metadata?.industry_shape || "").trim();
  const showIndustry = !!industry && industry.toLowerCase() !== "general";

  const screenLabels = (schema.screens || [])
    .map((s) => (s.label || "").trim())
    .filter(Boolean)
    .slice(0, 3);
  const screensList =
    screenLabels.length === 0
      ? ""
      : screenLabels.length === 1
        ? screenLabels[0]
        : `${screenLabels.slice(0, -1).join(", ")} and ${screenLabels[screenLabels.length - 1]}`;

  const brandColor = schema.metadata?.brand_color || DEFAULT_BRAND_COLOR;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-[#0b0b0b] text-[#f5f1ea] antialiased px-6">
      <div className="max-w-xl w-full text-center">
        <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#6b6b63] mb-7">
          {businessName}
        </div>
        <h1 className="text-3xl font-semibold tracking-tight mb-4">
          Here's your Agentic Operating System, {businessName}.
        </h1>
        <p className="text-[15px] leading-relaxed text-[#a8a29a] mb-9">
          {showIndustry ? `Built for ${industry}. ` : ""}
          {screensList
            ? `Your ${screensList} ${screenLabels.length === 1 ? "screen is" : "screens are"} live. `
            : ""}
          Your Operator already knows your mission. First move: tell it where your live data lives, then it can help connect the right source.
        </p>
        <button
          onClick={onEnter}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#f5f1ea] text-[#0b0b0b] font-semibold text-sm transition-transform hover:scale-[1.02]"
          style={{ boxShadow: `0 0 0 1px ${brandColor}40` }}
        >
          Open Operator →
        </button>
      </div>
    </div>
  );
}
